import { Favorite } from '../models/WeatherData';

export class StorageService {
  private static FAVORITES_KEY = 'weatherAppFavorites';
  private static SPECIES_KEY = 'targetSpecies';
  private static WATER_PROFILE_KEY = 'waterProfile';

  static getFavorites(): Favorite[] {
    try {
      const favorites = localStorage.getItem(this.FAVORITES_KEY);
      return favorites ? JSON.parse(favorites) : [];
    } catch (error) {
      console.error('Fel vid läsning av favoriter:', error);
      return [];
    }
  }

  static saveFavorites(favorites: Favorite[]): boolean {
    try {
      localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(favorites));
      return true;
    } catch (error) {
      console.error('Fel vid sparning av favoriter:', error);
      return false;
    }
  }

  static addFavorite(lat: number, lng: number, name: string): boolean {
    const favorites = this.getFavorites();
    
    const exists = favorites.find(fav => 
      Math.abs(fav.lat - lat) < 0.01 && Math.abs(fav.lng - lng) < 0.01
    );
    
    if (exists) {
      return false;
    }
    
    if (favorites.length >= 10) {
      return false;
    }
    
    const newFavorite: Favorite = {
      id: Date.now(),
      lat: parseFloat(lat.toFixed(4)),
      lng: parseFloat(lng.toFixed(4)),
      name: name || `${lat.toFixed(2)}, ${lng.toFixed(2)}`,
      addedAt: new Date().toISOString()
    };
    
    favorites.unshift(newFavorite);
    return this.saveFavorites(favorites);
  }

  static removeFavorite(id: number): boolean {
    const favorites = this.getFavorites();
    const filtered = favorites.filter(fav => fav.id !== id);
    return this.saveFavorites(filtered);
  }

  static getTargetSpecies(): string {
    const species = localStorage.getItem(this.SPECIES_KEY) || 'general';
    return species.toLowerCase();
  }

  static setTargetSpecies(species: string): void {
    localStorage.setItem(this.SPECIES_KEY, species.toLowerCase());
  }

  static getWaterProfile(): { depth: string; clarity: string } {
    try {
      const profile = localStorage.getItem(this.WATER_PROFILE_KEY);
      return profile ? JSON.parse(profile) : { depth: 'shallow', clarity: 'stained' };
    } catch {
      return { depth: 'shallow', clarity: 'stained' };
    }
  }

  static setWaterProfile(profile: { depth: string; clarity: string }): void {
    localStorage.setItem(this.WATER_PROFILE_KEY, JSON.stringify(profile));
  }

  // AI Fishing Questions - stored per location
  private static AI_QUESTIONS_KEY = 'aiFishingQuestions';

  private static getLocationKey(lat: number, lng: number): string {
    // Round to 2 decimals to group nearby locations
    return `${lat.toFixed(2)}_${lng.toFixed(2)}`;
  }

  static getLocationQuestions(lat: number, lng: number): { fromShore: boolean; method: string; waterClarity: string } | null {
    try {
      const allQuestions = localStorage.getItem(this.AI_QUESTIONS_KEY);
      if (!allQuestions) return null;
      
      const questionsMap = JSON.parse(allQuestions);
      const locationKey = this.getLocationKey(lat, lng);
      
      return questionsMap[locationKey] || null;
    } catch (error) {
      console.error('Error reading AI questions:', error);
      return null;
    }
  }

  static saveLocationQuestions(lat: number, lng: number, questions: { fromShore: boolean; method: string; waterClarity: string }): boolean {
    try {
      const allQuestions = localStorage.getItem(this.AI_QUESTIONS_KEY);
      const questionsMap = allQuestions ? JSON.parse(allQuestions) : {};
      
      const locationKey = this.getLocationKey(lat, lng);
      questionsMap[locationKey] = questions;
      
      localStorage.setItem(this.AI_QUESTIONS_KEY, JSON.stringify(questionsMap));
      return true;
    } catch (error) {
      console.error('Error saving AI questions:', error);
      return false;
    }
  }

  // AI Conversations - stored per location, date, species, and language
  private static AI_CONVERSATIONS_KEY = 'aiConversations';

  private static getConversationKey(lat: number, lng: number, date: string, species: string, language: string): string {
    const locationKey = this.getLocationKey(lat, lng);
    return `${locationKey}_${date}_${species}_${language}`;
  }

  static getConversation(lat: number, lng: number, date: string, species: string, language: string): Array<{ role: 'user' | 'assistant'; content: string }> | null {
    try {
      const allConversations = localStorage.getItem(this.AI_CONVERSATIONS_KEY);
      if (!allConversations) return null;
      
      const conversationsMap = JSON.parse(allConversations);
      const conversationKey = this.getConversationKey(lat, lng, date, species, language);
      
      return conversationsMap[conversationKey] || null;
    } catch (error) {
      console.error('Error reading AI conversation:', error);
      return null;
    }
  }

  static saveConversation(lat: number, lng: number, date: string, species: string, language: string, messages: Array<{ role: 'user' | 'assistant'; content: string }>): boolean {
    try {
      const allConversations = localStorage.getItem(this.AI_CONVERSATIONS_KEY);
      const conversationsMap = allConversations ? JSON.parse(allConversations) : {};
      
      const conversationKey = this.getConversationKey(lat, lng, date, species, language);
      conversationsMap[conversationKey] = messages;
      
      localStorage.setItem(this.AI_CONVERSATIONS_KEY, JSON.stringify(conversationsMap));
      return true;
    } catch (error) {
      console.error('Error saving AI conversation:', error);
      return false;
    }
  }
}
